import { ISessionApi, IViewportApi } from "@shapediver/viewer";
import jsPDF from "jspdf";
import { getPriceOutput } from "../components/menu/menuCreation";
import { base64 } from "../base64";
import { getWeightValue } from "../components/collapsables/createReference";
import * as SDV from "@shapediver/viewer";
import { fetchConversionRate } from "./currencyConversion";

const titleBase64 = base64.title;
const footerBase64 = base64.footer;

const options = [
  { value: "USD", text: "USD", rate: 1 },
  { value: "AED", text: "AED", rate: 3.67 },
  { value: "GBP", text: "GBP", rate: 0.78 },
  { value: "EUR", text: "EUR", rate: 0.91 },
  { value: "SAR", text: "SAR", rate: 3.75 },
  { value: "KWD", text: "KWD", rate: 0.31 },
  { value: "OMR", text: "OMR", rate: 0.39 },
  { value: "QAR", text: "QAR", rate: 3.64 },
];

(<any>window).SDV = SDV;

/**
 * Creates a screenshot of the current scene.
 *
 * @returns {Promise<string>} The screenshot as a data url.
 */
const createScreenshot = async (name: string) => {
  // create viewport where the canvas is not shown
  const hiddenCanvas = document.createElement("canvas");
  hiddenCanvas.style.display = "none";
  const div = document.createElement("div");
  div.style.position = "absolute";
  div.style.top = "-5000px";
  div.appendChild(hiddenCanvas);
  document.body.appendChild(div);
  const hiddenViewport = await SDV.createViewport({
    id: "myHiddenViewport",
    canvas: hiddenCanvas,
  });

  const keywords = ["PERGOLA", "CABANA"];
  const zoomValue = keywords.some((keyword) =>
    name.toUpperCase().includes(keyword)
  )
    ? 0.8
    : 1.25;

  // change the settings of the viewport to how the screenshot should look like
  hiddenViewport.beautyRenderBlendingDuration = 1; // we set the blending duration to 0 to make sure the screenshot is taken after the first frame
  hiddenViewport.automaticResizing = false; // disable automatic resizing
  hiddenViewport.maximumRenderingSize = { width: 800, height: 400 }; // adjust the maximum rendering size to the size of the screenshot
  hiddenViewport.resize(800, 400); // resize the viewport to the size of the screenshot
  hiddenViewport.camera.zoomToFactor = zoomValue;
  hiddenViewport.camera.autoAdjust = true;
  hiddenViewport.camera?.zoomTo();
  // before version 2.12.2, the functions above has to be adjusted by the window.devicePixelRatio as follows:
  // hiddenViewport.resize(2000 / window.devicePixelRatio, 1000 / window.devicePixelRatio);

  // wait for the first frame to be rendered
  await new Promise<void>((resolve) => {
    const token = SDV.addListener(
      SDV.EVENTTYPE.RENDERING.BEAUTY_RENDERING_FINISHED,
      (e) => {
        if ((e as SDV.IViewportEvent).viewportId !== hiddenViewport.id) return;
        SDV.removeListener(token);
        resolve();
      }
    );
  });

  // create a screenshot
  const image = hiddenViewport.getScreenshot("png", 10);

  // remove the hidden viewport
  await hiddenViewport.close();

  // remove the hidden canvas
  div.removeChild(hiddenCanvas);

  // remove the div
  document.body.removeChild(div);

  return image;
};

export function getParamValue(session: ISessionApi, paramName: string) {
  const paramEntry = Object.entries(session.parameters).find(
    (item) => item[1].name === paramName
  );

  if (paramEntry) {
    const param = paramEntry[1];
    const index = parseInt(param.value, 10);

    if (!isNaN(index) && index >= 0 && index < param.choices.length) {
      return param.choices[index];
    } else {
      console.error("Invalid index or out of range");
      return null;
    }
  } else {
    console.error("Parameter not found");
    return null;
  }
}

export function getParamValueWithoutChoice(
  session: ISessionApi,
  paramName: string
) {
  const paramEntry = Object.entries(session.parameters).find(
    (item) => item[1].name === paramName
  );

  if (paramEntry) {
    const param = paramEntry[1];
    const value = param.value;

    return value ? value : null;
  }
}

const createPDF = async (viewport: IViewportApi, session: ISessionApi) => {
  const dynamicName = session.node.name.split("_").pop()?.toUpperCase();
  const dynamicPrice = getPriceOutput(session)?.[1].content[0].data;
  const convertedPrice = document.getElementById(
    "price-selector"
  ) as HTMLDivElement;
  const quantityInput = document.getElementById(
    "qty-value"
  ) as HTMLInputElement;
  const quantityInputValue = quantityInput.value;
  const materialOutput = Object.entries(session.outputs).find(
    (item) =>
      item[1].name === "Material" &&
      item[1].content?.some((prop) => prop.name === "Material")
  );

  const panelsOutput = Object.entries(session.outputs).find(
    (item) =>
      item[1].name === "Panels" &&
      item[1].content?.some((prop) => prop.name === "Panels")
  );

  const materialData = materialOutput
    ? materialOutput[1].content?.find((prop) => prop.name === "Material")?.data
    : "";

  const panelsData = panelsOutput
    ? panelsOutput[1].content?.find((prop) => prop.name === "Panels")?.data ||
      ""
    : "";

  // Get the current date
  const currentDate = new Date();
  const day = currentDate.getDate().toString().padStart(2, "0");
  const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
  const year = currentDate.getFullYear().toString().slice(-2);

  const selectedCurrency = document.getElementById(
    "currency-select"
  ) as HTMLSelectElement;

  const doc = new jsPDF();

  const padding = 12;
  const pageSize = doc.internal.pageSize;
  const pageWidth = pageSize.width;
  const pageHeight = pageSize.height;

  // Add the viewport image
  const viewportImageWidth = 175;
  const viewportImageHeight = 75;
  const viewportImageX =
    padding + (pageWidth - 2 * padding - viewportImageWidth) / 2;
  const viewportImageY = padding + 40; // Adjusted Y position

  // Define the box dimensions for the viewport image
  const viewportBoxWidth = 175; // Adjust as needed
  const viewportBoxHeight = 100; // Adjust as needed
  const viewportBoxX =
    padding + (pageWidth - 2 * padding - viewportBoxWidth) / 2;
  const viewportBoxY = padding + 30; // Adjusted Y position

  // THIS REQUIRES HELP FROM SHAPEDIVER'S TEAM
  // Get the viewport image
  const viewportImage = await createScreenshot(dynamicName || "");
  // Calculate the aspect ratio of the viewport image
  const originalViewportImgWidth = 1240; // Adjust based on actual image dimensions
  const originalViewportImgHeight = 700; // Adjust based on actual image dimensions
  const viewportAspectRatio =
    originalViewportImgWidth / originalViewportImgHeight;

  // Calculate the maximum width and height the image can have while maintaining its aspect ratio
  let drawViewportImgWidth, drawViewportImgHeight;

  // Adjust the logic to prioritize height if width is limiting
  if (viewportBoxWidth / viewportAspectRatio > viewportBoxHeight) {
    drawViewportImgHeight = viewportBoxHeight;
    drawViewportImgWidth = viewportBoxHeight * viewportAspectRatio;
  } else {
    drawViewportImgWidth = viewportBoxWidth;
    drawViewportImgHeight = viewportBoxWidth / viewportAspectRatio;
  }

  // Calculate the position to center the image within the rectangle
  const drawViewportImgX =
    viewportBoxX + (viewportBoxWidth - drawViewportImgWidth) / 2;
  const drawViewportImgY =
    viewportBoxY + (viewportBoxHeight - drawViewportImgHeight) / 2;

  // Add the image
  doc.addImage(
    viewportImage,
    "PNG",
    drawViewportImgX,
    drawViewportImgY - 15,
    drawViewportImgWidth,
    drawViewportImgHeight
  );

  const imgWidth = 50; // Adjust as needed
  const imgHeight = 20; // Adjust as needed
  const imgX = viewportImageX + (viewportImageWidth - imgWidth) / 2; // Center over the viewport image
  const imgY = 100; // Center over the viewport image

  // Add the image inside the rectangle
  const originalImgWidth = 1240; // Original image width (adjust based on your image)
  const originalImgHeight = 480; // Original image height (adjust based on your image)
  const aspectRatio = originalImgWidth / originalImgHeight;

  // Calculate the maximum width and height the image can have while maintaining its aspect ratio
  let drawImgWidth, drawImgHeight;

  if (imgWidth / aspectRatio < imgHeight) {
    drawImgWidth = imgWidth;
    drawImgHeight = imgWidth / aspectRatio;
  } else {
    drawImgWidth = imgHeight * aspectRatio;
    drawImgHeight = imgHeight;
  }

  // Calculate the position to center the image within the rectangle
  const drawImgX = imgX + (imgWidth - drawImgWidth) / 2;
  const drawImgY = imgY - 85 + (imgHeight - drawImgHeight) / 2;

  // Add the image
  doc.addImage(
    titleBase64,
    "png",
    drawImgX,
    drawImgY - 10,
    drawImgWidth,
    drawImgHeight
  );

  // Text below viewport image
  const text =
    "These prices are valid for 10 days from creation date and are ex works.";
  const textFontSize = 10;
  const textWidth =
    (doc.getStringUnitWidth(text) * textFontSize) / doc.internal.scaleFactor;
  const textX = pageWidth - padding - textWidth - 10; // Align to the right
  const textY = viewportImageY + viewportImageHeight + 20; // Adjusted position below the viewport image
  doc.setFontSize(textFontSize);
  doc.setTextColor(0, 0, 0);

  // Additional Information
  const convertedPriceRate = options.find(option => option.value === selectedCurrency.value)?.rate.toFixed(2).toString() 
  const pergolaInfo = dynamicName || "Placeholder";
  const unitPrice = `${selectedCurrency.value || "$"}: ${
    (dynamicPrice * convertedPriceRate || 1).toFixed(2)
  }`;
  const createdOn = "Created On:";
  const creationDate = `${day}.${month}.${year}`;
  const quantity = "Quantity:";
  const quantityValue = quantityInputValue;
  const pergolaFontSize = 20; // Font size for PERGOLA
  const infoFontSize = 10; // Font size for other text
  const infoX = padding + 6; // Align to the left
  const infoY = textY; // Adjusted position below the text

  doc.setFontSize(pergolaFontSize);
  doc.setFont(undefined, "bold");
  doc.text(pergolaInfo, infoX, infoY);
  doc.setFont(undefined, "normal");

  // Set font size and color for other text
  doc.setFontSize(infoFontSize + 2);

  // Unit price
  const unitPriceWidth =
    (doc.getStringUnitWidth(unitPrice) * infoFontSize) /
    doc.internal.scaleFactor;
  doc.text(unitPrice, infoX, infoY + 8);
  doc.text(
    "per unit",
    infoX + 45,
    infoY + 8
  );
  doc.setFontSize(infoFontSize + 2);

  // Created On and Creation Date
  const createdOnWidth =
    (doc.getStringUnitWidth(createdOn) * infoFontSize) /
    doc.internal.scaleFactor;
  const creationDateWidth =
    (doc.getStringUnitWidth(creationDate) * infoFontSize) /
    doc.internal.scaleFactor;

  // Quantity and Quantity value
  const quantityWidth =
    (doc.getStringUnitWidth(quantity) * infoFontSize) /
    doc.internal.scaleFactor;
  const quantityValueWidth =
    (doc.getStringUnitWidth(quantityValue) * infoFontSize) /
    doc.internal.scaleFactor;

  // Calculate the combined width of Quantity and its value
  const totalQuantityWidth = Math.max(quantityWidth, quantityValueWidth);

  // Position Quantity and Quantity value together on the right side
  const quantityX = pageWidth - padding - totalQuantityWidth - 43;
  doc.text(quantity, infoX, infoY + 13);
  doc.text(quantityValue, infoX + 45, infoY + 13);

  const totalPrice = document.getElementById("price-selector") as HTMLDivElement;
  doc.text(`${selectedCurrency.value || "$"}: ${
    totalPrice.innerText
  }`, infoX, infoY + 18)
  doc.text(`total price`, infoX + 45, infoY + 18)
  let startingY = 185; // Starting Y position
  let startingX = 18;
  if (String(materialData).length > 2) {
    doc.text(`Material:`, infoX, infoY + 23)
    doc.text(materialData, infoX + 45, infoY + 23)
  }
  if (String(panelsData).length > 2) {
    doc.text(`Panels:`, infoX, infoY + 28)
    doc.text(panelsData, infoX + 45, infoY + 28)
  }

  // Convert session.parameters to an array and filter out "Bool" types
  const filteredParameters = Object.entries(session.parameters)
    .filter(
      ([id, parameterApi]) =>
        parameterApi.type !== "Bool" && parameterApi.name !== "Units"
    )
    .map(([id, parameterApi]) => parameterApi);

  // Sort the filtered parameters based on the order value
  const sortedParameters = filteredParameters.sort((a, b) => a.order - b.order);

  // Group parameters by 'group'
  const groupedParameters = sortedParameters.reduce((acc, param) => {
    const group = param.group ? param.group.name || "Ungrouped" : "Ungrouped";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(param);
    return acc;
  }, {});

  const groupedX = 26; // Adjusted X position for grouped parameters

  // Function to process each parameter
  Object.entries(groupedParameters).forEach(([groupName, parameters]) => {
    if (groupName === "Group") return;

    // Determine the X position based on the group name
    let xPosition = groupName !== "Ungrouped" ? groupedX - 8 : startingX;
    let yPosition = startingY + 10; // Default starting Y position

    // Check if the group name is "Base Shape" and adjust X and Y position accordingly
    if (groupName === "Base Shape") {
      xPosition = 125; // New X position for "Base Shape" group
      yPosition = 185; // New Y position for "Base Shape" group

      // Set font size and color for "Base Shape" group name
      doc.setFontSize(infoFontSize + 2);
      doc.setTextColor(0, 0, 0); // Black color
      doc.setFont(undefined, "bold");
      doc.text(groupName, xPosition, yPosition);
      yPosition += 5; // Adjust space after the group name
    } else {
      // Set font size and color for other group names
      if (groupName !== "Ungrouped") {
        doc.setFontSize(infoFontSize + 2);
        doc.setTextColor(0, 0, 0); // Black color
        doc.setFont(undefined, "bold");
        doc.text(groupName, xPosition, startingY);
        startingY += 5; // Adjust space after the group name
      }
    }

    // Process each parameter in the group
    parameters.forEach((parameterApi) => {
      const label = parameterApi.name || "Unnamed Parameter";
      let value = parameterApi.value;

      // Check if the value is an index and get the corresponding choice if applicable
      if (parameterApi.choices && !isNaN(value)) {
        value = parameterApi.choices[parseInt(value, 10)];
      }

      // Append units to the value for specific parameters
      switch (parameterApi.name) {
        case "Length":
        case "Width":
        case "Height":
        case "Side Height":
        case "Border":
        case "Base Border":
        case "Side Border - Top":
        case "Side Border - Bottom":
        case "Corner Border":
          value += " cm";
          break;
        case "Material Thickness":
          value += " mm";
          break;
        // Add more cases as needed
      }

      // Combine label and value for the output
      const labelText = `${label}:`;
      const valueText = `${value}`;

      // Set font size and color
      doc.setTextColor(0, 0, 0); // Black color

      // Position and draw the text
      if (groupName === "Base Shape") {
        doc.setFont(undefined, "bold");
        doc.text(labelText, xPosition, yPosition);
        doc.setFont(undefined, "normal");
        doc.text(valueText, xPosition + 45, yPosition);
        yPosition += 5; // Adjust Y position for the next parameter within "Base Shape"
      } else if (groupName !== "Ungrouped") {
        doc.setFont(undefined, "bold");
        doc.text(labelText, xPosition, startingY);
        doc.setFont(undefined, "normal");
        doc.text(valueText, xPosition + 45, startingY);
        startingY += 5; // Adjust Y position for the next parameter
      } else {
        doc.setFont(undefined, "bold");
        doc.text(labelText, xPosition, startingY);
        doc.setFont(undefined, "normal");
        doc.text(valueText, xPosition + 45, startingY);
        startingY += 5; // Adjust Y position for the next parameter
      }
    });

    // Add extra space after each group
    startingY += 5;
  });

  // Add the base64 footer image at the bottom
  const footerImgWidth = 230; // 100% width inside the rectangle
  const footerImgHeight = 50; // Adjust as needed
  const footerImgX = padding - 19;
  const footerImgY = pageHeight - padding - footerImgHeight + 25; // Positioned at the bottom of the rectangle
  doc.addImage(
    footerBase64,
    "JPEG",
    footerImgX,
    footerImgY,
    footerImgWidth,
    footerImgHeight
  );


  doc.setFontSize(8)
  doc.text(createdOn, infoX, footerImgY - 10);
  doc.text(creationDate, infoX + createdOnWidth + 1, footerImgY - 10);
  doc.text(text, footerImgX + 25, footerImgY - 5);
  doc.save(`ABIYA - ${dynamicName}.pdf`);
};

export const handleExportPdf = async (
  session: ISessionApi,
  viewport: IViewportApi
) => {
  createPDF(viewport, session);
};

export const handleExportDwg = async (session: ISessionApi) => {
  const getRefBox = document.getElementById("ref-box-id") as HTMLDivElement;
  if (getRefBox) getRefBox.style.zIndex = "1";
  let email = "";
  let fullName = ""
  let companyName = ""
  let country = ""
  let phoneNumber = ""

  function clearInputs() {
    const inputs = [
      "email-value",
      "fullname-value",
      "company-value",
      "country-value",
      "phone-value"
    ];
  
    inputs.forEach(id => {
      const input = document.getElementById(id) as HTMLInputElement;
      if (input) {
        input.value = "";
      }
    });
  
    // Reset variables to ensure the form is validated correctly after clearing inputs
    email = "";
    fullName = "";
    companyName = "";
    country = "";
    phoneNumber = "";
  
    // Revalidate the form to update the download button state
    validateForm();
  }

  const validateForm = () => {
    const isFormValid =
      email.length > 0 &&
      fullName.length > 0 &&
      companyName.length > 0 &&
      country.length > 0 &&
      phoneNumber.length > 0;
    downloadBtn.disabled = !isFormValid;
  };
  const getModal = document.getElementById("email-modal") as HTMLDivElement;
  const getCloseBtn = document.getElementById(
    "close-btn-email"
  ) as HTMLButtonElement;
  const modalContent = document.getElementById(
    "email-content-modal"
  ) as HTMLDivElement;
  const checkIfInputIsCreated = document.getElementById(
    "email-value"
  ) as HTMLInputElement;
  const downloadBtn = document.getElementById(
    "dwg-btn-email"
  ) as HTMLButtonElement;

  if (!checkIfInputIsCreated) {
    const input = document.createElement("input") as HTMLInputElement;
    modalContent.appendChild(input);
    input.type = "email";
    input.placeholder = "youremail@gmail.com";
    input.id = "email-value";
    input.addEventListener("input", (event: Event) => {
      email = (event.target as HTMLInputElement).value;
      validateForm();
    });
  
    const inputFullName = document.createElement("input") as HTMLInputElement;
    modalContent.appendChild(inputFullName);
    inputFullName.type = "text";
    inputFullName.placeholder = "Your Name";
    inputFullName.id = "fullname-value";
    inputFullName.addEventListener("input", (event: Event) => {
      fullName = (event.target as HTMLInputElement).value;
      validateForm();
    });
  
    const inputCompanyName = document.createElement("input") as HTMLInputElement;
    modalContent.appendChild(inputCompanyName);
    inputCompanyName.type = "text";
    inputCompanyName.placeholder = "Your Company";
    inputCompanyName.id = "company-value";
    inputCompanyName.addEventListener("input", (event: Event) => {
      companyName = (event.target as HTMLInputElement).value;
      validateForm();
    });
  
    const inputCountry = document.createElement("input") as HTMLInputElement;
    modalContent.appendChild(inputCountry);
    inputCountry.type = "text";
    inputCountry.placeholder = "Your Country";
    inputCountry.id = "country-value";
    inputCountry.addEventListener("input", (event: Event) => {
      country = (event.target as HTMLInputElement).value;
      validateForm();
    });
  
    const inputPhoneNumber = document.createElement("input") as HTMLInputElement;
    modalContent.appendChild(inputPhoneNumber);
    inputPhoneNumber.type = "text";
    inputPhoneNumber.placeholder = "Your Phone Number";
    inputPhoneNumber.id = "phone-value";
    inputPhoneNumber.addEventListener("input", (event: Event) => {
      phoneNumber = (event.target as HTMLInputElement).value;
      validateForm();
    });
  }
  
  // Initial validation check
  validateForm();
  downloadBtn.addEventListener("click", async () => {
      if (email === "Abiya_download!") {
      const getExport = session?.getExportByName("Export DWG Drawing")[0];
      const exportData = await getExport?.request();
      if (exportData?.content && exportData?.content[0]) {
        window.open(exportData.content[0].href);
      }
      clearInputs();
      getModal.style.display = "none";
    } else {
      const spinner = document.getElementById("spinner2") as HTMLDivElement;
      spinner.style.display = "flex"
      downloadBtn.disabled = true;
      getCloseBtn.disabled = true;
      const getExport = session?.getExportByName("Export DWG Drawing")[0];
      const exportData = await getExport?.request();
      if (exportData?.content && exportData?.content[0]) {
        const data = {
          email: email,
          fullName: fullName,
          company: companyName,
          country: country,
          phoneNumber: phoneNumber,
          dwgLink: exportData.content[0].href // Replace with actual DWG link
      };
      try {
        const response = await fetch('https://be-email.vercel.app/api/send-email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
    
        var x = document.getElementById("snackbar");
        if (!x) return;
        if (response.ok) {
            // If the request was successful
            x.className = "show";
            x.style.backgroundColor = "rgb(14, 12, 12);"
            x.innerText = "YOUR REQUEST HAS BEEN SENT";
    
            // After 3 seconds, remove the show class from DIV
            setTimeout(function() {
              if (!x) return;
                x.className = x.className.replace("show", "");
            }, 3000);
        } else {
            // If the request was not successful
            x.className = "show";
            x.style.backgroundColor = "#f72858"
            x.innerText = "THERE WAS AN ERROR, PLEASE TRY AGAIN";
    
            // After 3 seconds, remove the show class from DIV
            setTimeout(function() {
              if (!x) return;
                x.className = x.className.replace("show", "");
            }, 3000);
        }
    } catch (error) {
        // If there was a network error or some other issue
        var x = document.getElementById("snackbar");
        if (!x) return;
        x.className = "show";
        x.style.backgroundColor = "#f72858"
        x.innerText = "THERE WAS AN ERROR, PLEASE TRY AGAIN";
    
        // After 3 seconds, remove the show class from DIV
        setTimeout(function() {
          if (!x) return;
            x.className = x.className.replace("show", "");
        }, 3000);
    }
      }
      getModal.style.display = "none"
      clearInputs()
      downloadBtn.disabled = false;
      getCloseBtn.disabled = false;
      spinner.style.display = "none";
    }
  });

  getCloseBtn.addEventListener("click", () => {
    getModal.style.display = "none";
    const getRefBox = document.getElementById("ref-box-id") as HTMLDivElement;
    if (getRefBox) getRefBox.style.zIndex = "99999";
    clearInputs()
  });

  getModal.style.display = "flex";
};



